import { z } from 'zod';
import type { IntegrationMode } from './invoicing';

/**
 * Płatności online faktur przez bramkę ING Pay (docs/24). Konfiguracja per firma (Ustawienia → Firmy → integracje),
 * sekrety (token autoryzacyjny, klucz sklepu) szyfrowane jak token Fakturowni.
 */

const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

export const ingPayUpdateSchema = z.object({
  /** wylaczona / test (sandbox) / produkcja */
  mode: z.enum(['wylaczona', 'test', 'produkcja']),
  /** „Identyfikator klienta” z panelu ING Pay (Sklepy → Szczegóły → Dane do integracji) */
  merchantId: z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9]{1,40}$/, 'Identyfikator klienta: litery i cyfry z panelu ING Pay'),
  /** „Identyfikator sklepu” (UUID) */
  serviceId: z.string().trim().toLowerCase().regex(UUID_V4, 'Identyfikator sklepu: UUID z panelu ING Pay'),
  /** nowy token autoryzacyjny — tylko na wejściu; pusty = bez zmian */
  token: z.string().trim().max(500).optional(),
  /** nowy klucz sklepu (do podpisu powiadomień) — tylko na wejściu; pusty = bez zmian */
  serviceKey: z.string().trim().max(200).optional(),
});
export type IngPayUpdateInput = z.infer<typeof ingPayUpdateSchema>;

export interface IngPayConnectionDto {
  companyId: string;
  provider: 'ing';
  mode: IntegrationMode;
  merchantId: string;
  serviceId: string;
  /** ostatnie znaki tokenu; pusty = brak */
  tokenHint: string;
  /** ostatnie znaki klucza sklepu; pusty = brak */
  serviceKeyHint: string;
  status: 'nieskonfigurowana' | 'ok' | 'blad';
  lastCheckAt: string | null;
  lastError: string;
  keyConfigured: boolean;
  /** adres powiadomień do wpisania w panelu ING Pay (Sklepy → Szczegóły → Dane do integracji → Adres notyfikacji) */
  notificationUrl: string;
  /** czy przyciski „Zapłać online” są widoczne (tryb włączony, sekrety zapisane, ostatnie sprawdzenie udane) */
  active: boolean;
}

export interface IngPayCheckDto {
  ok: boolean;
  message: string;
  /** aktywne metody płatności sklepu (np. „BLIK”, „Karta”) */
  methods: string[];
}

/** Strona płatności z linku w e-mailu (bez danych osobowych): co i ile do zapłaty. */
export interface PublicPaymentDto {
  /** do_zaplaty — można zapłacić; oplacona — nic do zapłaty; w_toku — płatność online czeka na potwierdzenie z banku;
   *  niedostepna — płatność online wyłączona albo dokumentu nie da się opłacić online */
  state: 'do_zaplaty' | 'oplacona' | 'w_toku' | 'niedostepna';
  sellerName: string;
  documentLabel: string;
  number: string;
  toPayGr: number;
  paymentTo: string | null;
}

/** Nadpłata z płatności online (klient zapłacił więcej, niż zostało) — do zwrotu w panelu ING Pay. */
export interface OnlineOverpaymentDto {
  id: string;
  amountGr: number;
  transactionId: string;
  invoiceNumber: string | null;
  createdAt: string;
  resolvedAt: string | null;
  resolvedByName: string | null;
  note: string;
}

export const overpaymentResolveSchema = z.object({ note: z.string().trim().max(500).default('') });
export type OverpaymentResolveInput = z.infer<typeof overpaymentResolveSchema>;
